// Use relative URLs in production, absolute in development
const API_BASE_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

class AuthService {
  constructor() {
    this.tokenKey = 'zgpt_auth_token';
    this.userKey = 'zgpt_user_data';
  }

  // Get stored auth token
  getToken() {
    return localStorage.getItem(this.tokenKey);
  }

  // Store auth token
  setToken(token) {
    localStorage.setItem(this.tokenKey, token);
  }

  // Remove auth token
  removeToken() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
  }

  // Get stored user data
  getUser() {
    const userData = localStorage.getItem(this.userKey);
    return userData ? JSON.parse(userData) : null;
  }

  // Store user data
  setUser(user) {
    localStorage.setItem(this.userKey, JSON.stringify(user));
  }

  // Get headers with auth token
  getAuthHeaders() {
    const token = this.getToken();
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Token ${token}`;
    }
    
    return headers;
  }

  // Login with username and password
  async login(username, password) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username,
          password: password
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        this.setToken(data.token);
        this.setUser(data.user);
        return data;
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // Check authentication status
  async checkAuthStatus() {
    try {
      const token = this.getToken();
      if (!token) {
        return {
          authenticated: false,
          user: null,
          error: 'No auth token found'
        };
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/status/`, {
        method: 'GET',
        credentials: 'include',
        headers: this.getAuthHeaders(),
      });
      
      if (response.ok) {
        const data = await response.json();
        this.setUser(data.user);
        return data;
      } else if (response.status === 401) {
        // Token is invalid, clear it
        this.removeToken();
        return {
          authenticated: false,
          user: null,
          error: 'Token expired or invalid'
        };
      } else {
        throw new Error('Failed to check auth status');
      }
    } catch (error) {
      console.error('Auth status check error:', error);
      return {
        authenticated: false,
        user: null,
        error: error.message
      };
    }
  }

  // Logout user
  async logout() {
    try {
      const token = this.getToken();
      if (token) {
        await fetch(`${API_BASE_URL}/api/auth/logout/`, {
          method: 'POST',
          credentials: 'include',
          headers: this.getAuthHeaders(),
        });
      }
      
      // Clear local storage regardless of API response
      this.removeToken();
      
      return { message: 'Successfully logged out' };
    } catch (error) {
      console.error('Logout error:', error);
      // Clear local storage even if API call fails
      this.removeToken();
      throw error;
    }
  }

  // Check if user is authenticated (local check)
  isAuthenticated() {
    return !!this.getToken();
  }

  // Development helper - create test user
  async createTestUser(userData) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/create-test-user/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });
      
      if (response.ok) {
        return await response.json();
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create test user');
      }
    } catch (error) {
      console.error('Create test user error:', error);
      throw error;
    }
  }

  // Get list of users for development/admin
  async listUsers() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/list-users/`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      
      if (response.ok) {
        return await response.json();
      } else {
        throw new Error('Failed to get users list');
      }
    } catch (error) {
      console.error('List users error:', error);
      return { users: [] };
    }
  }

  // Redirect to login page (for single page apps)
  redirectToLogin(currentPath = '/') {
    // For SPA, you might want to set a login route instead
    // window.location.href = '/login';
    console.log('Redirect to login needed for path:', currentPath);
  }
}

export default new AuthService();